<script setup lang="ts">
import { computed, nextTick, ref } from "vue";
import { ChevronDown, Settings, X } from "@lucide/vue";

import { v4 as uuidv4 } from "uuid";

import InputLabel from "./InputLabel.vue";
import { useListboxNavigation } from "./useListboxNavigation.composable";

import { useModalStore } from "@/stores/modals/modals.store";

// `deleted` only applies to custom rows. When a custom source is soft-deleted,
// it is excluded from the selectable list but kept in the lookup so projects
// that already reference it can render their selection as "<name> (deleted)"
// in the trigger.
type SourceOption = { id: string; name: string; deleted?: boolean };

// Single-select dropdown — never typeable. Creating custom rows happens
// exclusively in `CustomDataSourcesModal`, reachable from a "Manage custom
// data sources" entry inside the Custom group at the top of the listbox.

const props = withDefaults(
  defineProps<{
    /** Currently selected source id (built-in OR custom). Empty string == nothing selected. */
    modelValue?: string | null;
    /** Built-in emission factor sources (already filtered for the relevant supply type by the parent). */
    builtInSources: SourceOption[];
    /** Custom emission factor sources (already filtered for the relevant supply type by the parent). */
    customSources?: SourceOption[];
    label?: string;
    placeholder?: string;
    required?: boolean;
    isDisabled?: boolean;
    errorMsg?: string;
    /** Mirrors SelectInputWithKeyValue: when true and required and empty, parent paints a red border via errorMsg. */
    wasValidated?: boolean;
  }>(),
  {
    customSources: () => [],
    label: undefined,
    placeholder: "Select data source",
    required: false,
    isDisabled: false,
    errorMsg: undefined,
    wasValidated: false,
    modelValue: "",
  }
);

const emits = defineEmits<{
  (e: "update:modelValue", value: string): void;
}>();

const id = ref<string>(uuidv4());
const triggerRef = ref<HTMLButtonElement | null>(null);

const modals = useModalStore();

const allSources = computed<Array<SourceOption & { isCustom: boolean }>>(() => [
  ...props.builtInSources.map((source) => ({ ...source, isCustom: false })),
  ...props.customSources.map((source) => ({ ...source, isCustom: true })),
]);

const selectedSource = computed(() => allSources.value.find((source) => source.id === props.modelValue) ?? null);

// Active (non-soft-deleted) custom sources — what the listbox actually offers
// the user. Soft-deleted rows stay in `props.customSources` (and therefore in
// `allSources`) so the trigger can still resolve a previously-selected
// deleted source's name; they're filtered here so users can't pick one.
const activeCustomSources = computed(() => props.customSources.filter((source) => !source.deleted));

type FlatRow =
  | { kind: "built-in"; option: SourceOption; flatIndex: number }
  | { kind: "custom"; option: SourceOption; flatIndex: number }
  | { kind: "manage"; flatIndex: number };

// One flat list because that is what arrowing through the menu walks; the template splits it back
// into its groups. `flatIndex` is assigned here so both agree on which row is which.
const flatRows = computed<FlatRow[]>(() => {
  const rows: FlatRow[] = [];
  for (const option of props.builtInSources) {
    rows.push({ kind: "built-in", option, flatIndex: rows.length });
  }
  // Manage row sits at the top of the Custom group — semantically grouped
  // with the rows it manages, and the only entry point to the management
  // modal (replaces the per-row gear icon previously rendered next to the
  // label).
  rows.push({ kind: "manage", flatIndex: rows.length });
  for (const option of activeCustomSources.value) {
    rows.push({ kind: "custom", option, flatIndex: rows.length });
  }
  return rows;
});

const builtInRows = computed(() => flatRows.value.filter((row) => row.kind === "built-in"));
const customRows = computed(() => flatRows.value.filter((row) => row.kind === "custom"));
const manageRow = computed(() => flatRows.value.find((row) => row.kind === "manage") ?? null);

const rowId = (row: FlatRow) => `${id.value}-row-${row.flatIndex}`;

const selectExisting = (sourceId: string) => emits("update:modelValue", sourceId);

const openManageModal = () => modals.open("CustomDataSourcesModal");

const activate = (row: FlatRow) => {
  if (row.kind === "manage") openManageModal();
  else selectExisting(row.option.id);
};

const {
  root: containerRef,
  isOpen,
  activeIndex,
  activeOptionId,
  open,
  close,
  commit,
  handleKeydown,
} = useListboxNavigation<FlatRow>({
  items: flatRows,
  optionId: rowId,
  onCommit: activate,
  isDisabled: () => props.isDisabled,
  // Prefer highlighting the currently selected row so keyboard users land on their existing
  // selection, and otherwise the first row that is a source rather than the manage entry — so
  // opening the menu and pressing Enter never lands on the management modal.
  activeOnOpen: () => {
    const selected = flatRows.value.findIndex((row) => row.kind !== "manage" && row.option.id === props.modelValue);
    if (selected >= 0) return selected;
    const firstSelectable = flatRows.value.findIndex((row) => row.kind !== "manage");
    if (firstSelectable >= 0) return firstSelectable;
    return flatRows.value.length > 0 ? 0 : -1;
  },
  selectOnly: true,
});

const listboxId = computed(() => `${id.value}-listbox`);

const onTriggerClick = () => {
  if (props.isDisabled) return;
  isOpen.value ? close() : open();
};

const onClear = (event: MouseEvent) => {
  // Clear is a non-listbox affordance — stop the click from also toggling
  // the surrounding trigger button.
  event.stopPropagation();
  emits("update:modelValue", "");
  // Re-focus the trigger so keyboard users stay anchored to the field.
  nextTick(() => triggerRef.value?.focus());
};
</script>

<template>
  <div
    ref="containerRef"
    class="flex w-full flex-col">
    <div class="flex items-center gap-1">
      <InputLabel
        :id="id"
        :label="label"
        :required="required" />
      <!-- Trailing affordance beside the label, e.g. a link to the selected source's reference. -->
      <slot name="labelSuffix" />
    </div>

    <div class="relative w-full">
      <!--
        WAI-ARIA "Select-only" combobox pattern: a button trigger plus an
        adjacent listbox. No text input, no filtering — selection is
        keyboard- and pointer-driven only.
      -->
      <button
        :id="id"
        ref="triggerRef"
        type="button"
        role="combobox"
        :aria-haspopup="'listbox'"
        :aria-expanded="isOpen"
        :aria-controls="isOpen ? listboxId : undefined"
        :aria-activedescendant="activeOptionId"
        :aria-required="required || undefined"
        :aria-invalid="!!errorMsg || undefined"
        :disabled="isDisabled"
        class="focus:border-app-blue-600 rounded-8 t-small flex h-9 w-full items-center justify-between border py-2 pl-3 text-left leading-normal outline-hidden"
        :class="[
          { 'text-muted-text-color cursor-not-allowed bg-slate-200': isDisabled },
          { 'bg-slate-50': !isDisabled },
          errorMsg ? 'border-app-red-500' : 'border-slate-300',
          selectedSource?.isCustom && !isOpen ? 'pr-32' : 'pr-16',
        ]"
        data-cy="data-source-combobox-trigger"
        @click="onTriggerClick"
        @keydown="handleKeydown">
        <span
          v-if="selectedSource"
          class="truncate"
          data-cy="data-source-combobox-selected-name">
          {{ selectedSource.name }}
        </span>
        <span
          v-else
          class="text-type-muted truncate">
          {{ placeholder }}
        </span>
      </button>

      <!-- Trailing controls (badge + clear + chevron, sharing one flex row so they can't overlap) -->
      <div class="t-xs text-type-muted absolute top-0 right-0 flex h-full items-center gap-1 pr-2">
        <!--
          Soft-deleted custom rows surface as a `Deleted` badge in red instead
          of the regular `Custom` blue badge — clearer signal than a parenthetical
          suffix on the name and consistent with how the Custom badge already
          telegraphs "this is a tenant-scoped custom row".
        -->
        <span
          v-if="!isOpen && selectedSource && selectedSource.deleted"
          class="bg-app-red-50 text-app-red-700 t-sh7 rounded-full px-2 py-0.5 tracking-wide uppercase"
          data-cy="data-source-combobox-selected-deleted-badge">
          Deleted
        </span>
        <span
          v-else-if="!isOpen && selectedSource && selectedSource.isCustom"
          class="bg-app-blue-50 text-app-blue-700 t-sh7 rounded-full px-2 py-0.5 tracking-wide uppercase"
          data-cy="data-source-combobox-selected-custom-badge">
          Custom
        </span>
        <button
          v-if="!isDisabled && (modelValue ?? '') !== ''"
          type="button"
          class="flex h-6 w-6 items-center justify-center"
          aria-label="Clear data source"
          tabindex="-1"
          @mousedown.prevent
          @click="onClear">
          <X
            class="icon-sm"
            :stroke-width="1.5" />
        </button>
        <span class="pointer-events-none flex h-6 w-6 items-center justify-center">
          <ChevronDown
            class="icon-sm"
            :stroke-width="1.5"
            aria-hidden="true" />
        </span>
      </div>
    </div>

    <p
      v-if="errorMsg"
      class="text-app-red-700 t-xs">
      {{ errorMsg }}
    </p>

    <!-- Listbox -->
    <div class="relative z-10">
      <div
        v-if="isOpen"
        :id="listboxId"
        role="listbox"
        class="rounded-16 absolute z-999 max-h-60 w-full overflow-y-auto border border-slate-200 bg-slate-100 shadow-xl">
        <!-- Built-in group -->
        <div
          v-if="builtInSources.length > 0"
          role="group"
          aria-labelledby="builtin-heading">
          <div
            id="builtin-heading"
            class="text-type-muted t-sh6 bg-slate-200/60 px-3 py-1 tracking-wide uppercase">
            Built-in data sources
          </div>
          <div
            v-for="row in builtInRows"
            :id="rowId(row)"
            :key="`b-${row.option.id}`"
            role="option"
            :aria-selected="modelValue === row.option.id"
            class="t-small flex w-full cursor-pointer items-center gap-2 px-3 py-2"
            :class="{ 'bg-slate-300/60': activeIndex === row.flatIndex }"
            data-cy="data-source-combobox-option"
            @mouseenter="activeIndex = row.flatIndex"
            @mousedown.prevent="commit(row)">
            <span>{{ row.option.name }}</span>
          </div>
        </div>

        <!--
          Custom group — the section divider IS the manage entry. Visually it
          reads as a regular heading (uppercase, slate-tinted, semibold), but
          it's a `role="option"` row in the listbox flow: clickable, keyboard-
          reachable via Arrow keys, and skipped by `activeOnOpen` so opening the
          dropdown and pressing Enter still lands on a real source.
          The trailing gear icon and `aria-label` telegraph that it opens a
          separate modal rather than selecting a value.
        -->
        <div
          v-if="manageRow"
          :id="rowId(manageRow)"
          role="option"
          aria-label="Manage custom data sources"
          title="Manage custom data sources"
          :aria-selected="false"
          class="text-type-muted hover:text-type-default t-sh6 flex w-full cursor-pointer items-center gap-2 bg-slate-200/60 px-3 py-1 tracking-wide uppercase"
          :class="{ 'text-type-default bg-slate-300/60': activeIndex === manageRow.flatIndex }"
          data-cy="data-source-combobox-manage-option"
          @mouseenter="activeIndex = manageRow.flatIndex"
          @mousedown.prevent="commit(manageRow)">
          <Settings
            class="icon-xs"
            :stroke-width="1.5"
            aria-hidden="true" />
          <span>Custom data sources</span>
        </div>
        <!--
          Empty-state row — shown when there are no *active* custom rows.
          Soft-deleted rows in `customSources` are intentionally not counted
          here (they live in `customSources` only so the trigger can resolve
          a previously-selected deleted source's name).
        -->
        <div
          v-if="activeCustomSources.length === 0"
          class="text-type-muted t-xs px-3 py-2 italic"
          data-cy="data-source-combobox-empty-custom-hint">
          No custom data sources yet
        </div>
        <div
          v-for="row in customRows"
          :id="rowId(row)"
          :key="`c-${row.option.id}`"
          role="option"
          :aria-selected="modelValue === row.option.id"
          class="t-small flex w-full cursor-pointer items-center gap-2 px-3 py-2"
          :class="{ 'bg-slate-300/60': activeIndex === row.flatIndex }"
          data-cy="data-source-combobox-option"
          @mouseenter="activeIndex = row.flatIndex"
          @mousedown.prevent="commit(row)">
          <span>{{ row.option.name }}</span>
          <span class="bg-app-blue-50 text-app-blue-700 t-sh7 ml-auto rounded-full px-2 py-0.5 tracking-wide uppercase"
            >Custom</span
          >
        </div>
      </div>
    </div>
  </div>
</template>
